import React, { useEffect } from 'react';
import { FormikProps } from 'formik';
import { Flex, Box, Label, Container, Text } from 'theme-ui';
import { GrayFormikField } from '@/components/GrayFormikField';
import { ProductType } from '@/types/types';
import { useGetVossProductsQuery } from '@/store/apiService';
import Button from '@/components/Button';
import Error from '@/components/Error';
import CustomDatePicker from '@/components/CustomDatePicker';
import { formatArticleOption, getArticleInfo, PRODUCT_TYPE_BILLING } from './articleInfo';

// The API only uses the date part, so send the picked calendar day rather than a
// UTC instant, which would shift the day for timezones ahead of UTC.
const toLocalDateString = (date: Date) => {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
};

const fromLocalDateString = (value: string | null) => {
    if (!value) return null;

    const [year, month, day] = value.split('-').map(Number);

    return new Date(year, month - 1, day);
};

interface FormProps extends FormikProps<any> {
    additionalErrors?: React.ReactNode;
}

const articleTypesOptions = [
    {
        value: ProductType.Addon,
        label: 'Addon',
    },
    {
        value: ProductType.License,
        label: 'License',
    },
    {
        value: ProductType.Usage,
        label: 'Usage',
    },
    {
        value: ProductType.Main,
        label: 'Main',
    },
];

export const CreateUsageForm: React.FC<FormProps> = ({
    handleChange,
    additionalErrors,
    ...props
}) => {
    const articles = useGetVossProductsQuery({ ProductType: props.values._articleType });

    useEffect(() => {
        props.setFieldValue('ArticleNumber', '');
    }, [props.values._articleType]);

    const selectedArticle = articles.data?.find(
        (product) => product.ArticleNumber === props.values.ArticleNumber
    );
    const selectedInfo = getArticleInfo(selectedArticle?.ArticleNumber);
    const billingNote = PRODUCT_TYPE_BILLING[props.values._articleType as ProductType];

    return (
        <Flex backgroundColor="Background" sx={{ flexDirection: 'column', borderRadius: 'sm' }}>
            <Flex
                p="2"
                sx={{
                    gap: 2,
                    '@media only screen and (max-width:60em)': {
                        flexDirection: 'column',
                    },
                    borderRadius: 'sm',
                }}
            >
                <Box
                    sx={{
                        flexBasis: '50%',
                        borderRadius: 'sm',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: 'gray.2',
                        alignSelf: 'flex-start',
                    }}
                    p="3"
                >
                    <Label>Article type</Label>
                    <GrayFormikField
                        type="select"
                        name="_articleType"
                        placeholder="Select article type"
                        options={articleTypesOptions}
                    />

                    <Label>Article</Label>
                    <GrayFormikField
                        type="select"
                        name="ArticleNumber"
                        placeholder="Select article"
                        options={
                            articles.data
                                ? articles.data.map((product) => ({
                                      value: product.ArticleNumber,
                                      label: formatArticleOption(product),
                                  }))
                                : []
                        }
                        disabled={articles.isFetching}
                        isLoading={articles.isFetching}
                    />
                    {selectedArticle ? (
                        <Box
                            data-testid="article-details"
                            mb="3"
                            p="2"
                            sx={{
                                backgroundColor: 'gray.1',
                                borderRadius: 'sm',
                                borderLeft: '3px solid',
                                borderLeftColor: 'primary',
                                fontSize: 1,
                            }}
                        >
                            <Text sx={{ fontWeight: 'bold', display: 'block' }}>
                                {selectedInfo?.title ?? selectedArticle.Name}
                            </Text>
                            <Text sx={{ display: 'block', color: 'gray.7' }}>
                                Article number: {selectedArticle.ArticleNumber}
                            </Text>
                            {selectedInfo ? (
                                <>
                                    <Text sx={{ display: 'block' }}>
                                        {selectedInfo.description}
                                    </Text>
                                    <Text sx={{ display: 'block' }}>
                                        1 quantity = {selectedInfo.unit}
                                    </Text>
                                </>
                            ) : (
                                <Text sx={{ display: 'block', color: 'gray.7' }}>
                                    No description registered for this article.
                                </Text>
                            )}
                            {billingNote ? (
                                <Text sx={{ display: 'block', color: 'gray.7', mt: 1 }}>
                                    {billingNote}
                                </Text>
                            ) : null}
                        </Box>
                    ) : null}
                    <GrayFormikField
                        name="Description"
                        type="textarea"
                        label="Description"
                        placeholder="Shown on the invoice line, e.g. what was done and when"
                    />
                    <GrayFormikField
                        name="Quantity"
                        type="number"
                        label={selectedInfo ? `Quantity (1 = ${selectedInfo.unit})` : 'Quantity'}
                    />

                    <Label>Date</Label>
                    <CustomDatePicker
                        variation="normal"
                        field={{
                            name: 'Date',
                            value: fromLocalDateString(props.values.Date),
                        }}
                        onChange={(value: Date | null) => {
                            props.setFieldValue('Date', value ? toLocalDateString(value) : null);
                        }}
                    />

                    {additionalErrors ? (
                        <Box>
                            <Container variant="error-message">
                                <Error error={additionalErrors as any} />
                            </Container>
                        </Box>
                    ) : null}
                    <Flex
                        p="2"
                        sx={{
                            width: '100%',
                            gap: 2,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            isLoading={props.isSubmitting}
                            disabled={props.isSubmitting}
                            type="submit"
                            onClick={props.submitForm}
                        >
                            Save
                        </Button>
                        {/* TODO: add errors */}
                    </Flex>
                </Box>
            </Flex>
        </Flex>
    );
};
